import {Component, inject, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BimViewerService, ViewerModelSource} from "../../../services/bim-viewer/bim-viewer.service";
import {ActivatedRoute} from "@angular/router";
import {BicBimViewer} from "@buildinginformationcloud/bic-bim-viewer";
import {BicButtonComponent, BicInputComponent} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faLayerGroup, faMagnifyingGlass, faPlus} from "@fortawesome/pro-solid-svg-icons";
import {PageHeaderComponent} from "../design/page-header/page-header.component";

@Component({
  selector: 'app-bim-viewer',
  imports: [
    BicBimViewer,
    BicButtonComponent,
    BicInputComponent,
    FaIconComponent,
    PageHeaderComponent,
  ],
  templateUrl: './bim-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bim-viewer.component.scss'
})
export class BimViewerComponent implements OnDestroy, OnChanges {
  bicBimViewerService = inject(BimViewerService);
  activatedRoute = inject(ActivatedRoute)
  /**
   * Suchbegriff aus dem eigenen Header; wird an den Viewer durchgebunden und
   * filtert dort Modell- und Elementstruktur. Bewusst ein eigenes Feld und
   * nicht Teil von viewerConfig – sonst würde jeder Tastenanschlag ein neues
   * Config-Objekt binden.
   */
  protected structureSearchTerm = '';
  protected readonly faSearch = faMagnifyingGlass;


  /**
   * Hauptmodell. Traegt neben Name und URL die ProjectFile-Id, ueber die der Viewer-Tag gespeichert
   * wird -- vorher kamen hier nur `ifcUrl` und `fileName` an.
   */
  @Input() primaryModel: ViewerModelSource | null = null;
  /** Anzeigetitel: bei einem Koordinationsmodell dessen Name, nicht der des ersten Fachmodells. */
  @Input() title = '';
  @Input() additionalModels: ViewerModelSource[] = [];

  /**
   * Einmalig gelesen: als Getter im Template würde bei jedem Change-Detection-
   * Lauf ein neues Objekt an den Input gebunden.
   */
  protected readonly viewerConfig = this.bicBimViewerService.viewerConfig;
  protected readonly faPlus = faPlus;
  /** Modelle, die eingetroffen sind, bevor die Engine bereit war. */
  private pendingModels: ViewerModelSource[] = [];

  /** Der Modellname ist in der Engine die Modell-Id, doppelte Namen wuerden sich gegenseitig ersetzen. */
  private static dedupeByName(models: ViewerModelSource[]): ViewerModelSource[] {
    const seen = new Set<string>();
    return models.filter(model => {
      if (!model?.name || !model.url || seen.has(model.name)) return false;
      seen.add(model.name);
      return true;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    // If additionalModels changes after init and viewer is ready, load the new models
    if (changes['additionalModels'] && !changes['additionalModels'].firstChange) {
      const models = changes['additionalModels'].currentValue as ViewerModelSource[];
      if (models?.length > 0) {
        this.loadAdditionalModels(models);
      }
    }
  }

  /**
   * Die Engine wird von `<lib-bic-bim-viewer>` selbst aufgebaut; erst mit
   * (ready) darf geladen werden.
   */
  async onViewerReady(): Promise<void> {
    this.bicBimViewerService.onViewerReady();

    const models: ViewerModelSource[] = [];

    if (this.primaryModel?.url) {
      models.push(this.primaryModel, ...(this.additionalModels ?? []));
    }

    // Modelle, die während des Engine-Starts über ngOnChanges kamen
    models.push(...this.pendingModels);
    this.pendingModels = [];

    if (models.length === 0) return;

    const hasTopics = this.activatedRoute.snapshot.queryParams['topics'] == 'true';
    await this.bicBimViewerService.loadModelFromUrl(BimViewerComponent.dedupeByName(models), hasTopics);
  }

  private async loadAdditionalModels(models: ViewerModelSource[]): Promise<void> {
    if (!this.bicBimViewerService.isViewerReady) {
      this.pendingModels.push(...models);
      return;
    }

    await this.bicBimViewerService.loadModelFromUrl(BimViewerComponent.dedupeByName(models), false);
  }

  protected readonly faLayerGroup = faLayerGroup;

  protected get isViewerReady(): boolean {
    return this.bicBimViewerService.isViewerReady;
  }

  /** Öffnet die Projektdatei-Auswahl (siehe BimViewerService.openModelSelection). */
  protected openModelSelection(): void {
    this.bicBimViewerService.openModelSelection();
  }

  get isCoordinationView(): boolean {
    return this.bicBimViewerService?.isCoordinationView ?? false;
  }

  ngOnDestroy() {
    this.bicBimViewerService?.dispose();
  }

  createCoordinationModel() {
    this.bicBimViewerService.createCoordinationModel();
  }
}
